/*
 * eb-webinar-core.js — núcleo del directo del 11-nov (páginas /pages/webinar*).
 *
 * Dos partes:
 *  1. Lógica pura (sin DOM, con tests en tests/webinar/core.test.mjs):
 *     now, parseISO, landingState, roomState, phaseAt, countdownParts,
 *     resolveFuente, captureUtm/readUtm, googleCalendarUrl, outlookCalendarUrl,
 *     icsText, localTimeLabel, shareUrl, absUrl.
 *  2. Utilidades DOM que se montan solas (EBW.onReady) buscando atributos:
 *     [data-ebw-countdown]      cuenta atrás. data-target="start|doors|end|replayUntil".
 *                               Dígitos en [data-ebw-unit="d|h|m|s"]; texto sin JS en
 *                               .ebw-countdown__fallback. Al llegar a 0: .is-done + evento
 *                               "ebw:countdown-done".
 *     [data-ebw-calendar]       "google" | "outlook" (enlaces) | "ics" (botón; nace `hidden`).
 *     [data-ebw-share]          contenedor con [data-ebw-share-whatsapp], [data-ebw-share-copy],
 *                               [data-ebw-share-native], [data-ebw-share-url], [data-ebw-share-msg].
 *                               Atributos opcionales: data-text, data-content (utm_content).
 *     [data-ebw-landing-state]  pone data-state en <body> y muestra/oculta
 *                               [data-ebw-show-when~="<estado>"]. Evento "ebw:landing-state".
 *     [data-ebw-localtime]      "En tu zona horaria: {hora}" si la visitante no está en la hora
 *                               peninsular. data-template con {hora}; nace `hidden`.
 *     .ebw-reveal               aparición suave al hacer scroll (nunca oculta sin JS).
 *
 * Reloj: todo lo que depende de la hora usa EBW.now(). ?ebw_now=<ISO> lo fuerza para QA
 * (el reloj sigue corriendo desde ese instante). EBW.setServerOffset(ms) corrige un reloj
 * del dispositivo desajustado con la hora del servidor (lo usa la sala en directo).
 *
 * Exporta window.EBW (y module.exports en Node). ES2019, sin dependencias.
 */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) {
    root.EBW = Object.assign(root.EBW || {}, api);
    api._boot();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var MIN = 60000;
  var EVENT_TZ = 'Europe/Madrid';
  var EVENT_OFFSET_MIN = 60; // 11-nov-2026: horario de invierno, UTC+1
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var UTM_STORE = 'ebw_utm';

  function warn(where, err) {
    if (root.console && root.console.warn) root.console.warn('[EBW] ' + where, err);
  }

  function clamp01(x) {
    return x < 0 ? 0 : x > 1 ? 1 : x;
  }

  function num(x) {
    var n = Number(x);
    return isFinite(n) ? n : 0;
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* ------------------------------------------------------------------ */
  /* Fechas y reloj                                                      */
  /* ------------------------------------------------------------------ */

  // ISO 8601 con zona → ms UTC. Acepta "Z", "+01:00", "+0100" y el espacio en que la
  // query string convierte el "+". Sin zona devuelve NaN, salvo que se pase el desfase
  // por defecto en minutos (nunca se interpreta en la zona del navegador).
  function parseISO(s, defaultOffsetMin) {
    if (typeof s === 'number') return s;
    if (typeof s !== 'string') return NaN;
    var m = /^\s*(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?)?(Z|z|[+\- ]\d{2}(?::?\d{2})?)?\s*$/.exec(s);
    if (!m) return NaN;
    var month = +m[2];
    var day = +m[3];
    var hour = +(m[4] || 0);
    var minute = +(m[5] || 0);
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return NaN;
    var offset;
    var zone = m[8];
    if (zone === undefined) {
      if (typeof defaultOffsetMin !== 'number') return NaN;
      offset = defaultOffsetMin;
    } else if (zone === 'Z' || zone === 'z') {
      offset = 0;
    } else {
      var digits = zone.slice(1).replace(':', '');
      offset = (zone.charAt(0) === '-' ? -1 : 1) * (+digits.slice(0, 2) * 60 + +(digits.slice(2, 4) || 0));
    }
    var ms = +((m[7] || '') + '000').slice(0, 3);
    return Date.UTC(+m[1], month - 1, day, hour, minute, +(m[6] || 0), ms) - offset * MIN;
  }

  var qaOffset = null; // desfase forzado con ?ebw_now= (null = sin leer todavía)
  var serverOffset = 0; // desfase del reloj del dispositivo respecto al servidor

  function readQaOffset() {
    if (qaOffset !== null) return qaOffset;
    qaOffset = 0;
    try {
      var raw = root.location ? new URLSearchParams(root.location.search).get('ebw_now') : null;
      if (raw) {
        var forced = parseISO(raw, EVENT_OFFSET_MIN);
        if (!isNaN(forced)) qaOffset = forced - Date.now();
      }
    } catch (e) {
      warn('ebw_now', e);
    }
    return qaOffset;
  }

  function isQaClock() {
    return readQaOffset() !== 0;
  }

  function now() {
    var qa = readQaOffset();
    return Date.now() + (qa !== 0 ? qa : serverOffset);
  }

  function setServerOffset(ms) {
    serverOffset = isFinite(ms) ? +ms : 0;
  }

  // Instantes del evento con valores por defecto prudentes si falta alguno.
  function eventTimes(cfg) {
    var c = cfg || {};
    var start = parseISO(c.start);
    var doors = parseISO(c.doors);
    var end = parseISO(c.end);
    var replay = parseISO(c.replayUntil);
    if (isNaN(doors)) doors = start - 15 * MIN;
    if (isNaN(end)) end = start + 90 * MIN;
    if (isNaN(replay)) replay = end;
    return { start: start, doors: doors, end: end, replayUntil: replay };
  }

  // Estado de las landings de registro.
  function landingState(t, cfg) {
    var T = eventTimes(cfg);
    if (isNaN(T.start) || t < T.doors) return 'abierto';
    if (t < T.start) return 'ultima-hora';
    if (t < T.end) return 'en-directo';
    if (t < T.replayUntil) return 'grabacion';
    return 'cerrado';
  }

  // Estado de la sala del directo.
  function roomState(t, cfg) {
    var T = eventTimes(cfg);
    if (isNaN(T.start) || t < T.doors) return 'before';
    if (t < T.start) return 'doors';
    if (t < T.end) return 'live';
    return 'ended';
  }

  // Fase del programa a la hora t. phases = [{inicio_min, duracion_min}] (minutos desde startMs).
  // index -1 = todavía no ha empezado; en un hueco entre fases, la anterior queda completa.
  function phaseAt(t, startMs, phases) {
    var list = phases || [];
    var elapsed = (t - startMs) / MIN;
    if (!list.length || isNaN(elapsed)) return { index: -1, within: 0, overall: 0 };
    var index = -1;
    var best = -Infinity;
    var first = Infinity;
    var last = -Infinity;
    for (var i = 0; i < list.length; i++) {
      var s = num(list[i].inicio_min);
      var d = Math.max(0, num(list[i].duracion_min));
      if (s < first) first = s;
      if (s + d > last) last = s + d;
      if (s <= elapsed && s >= best) {
        best = s;
        index = i;
      }
    }
    var within = 0;
    if (index >= 0) {
      var dur = Math.max(0, num(list[index].duracion_min));
      within = dur > 0 ? clamp01((elapsed - best) / dur) : 1;
    }
    var span = last - first;
    var overall = span > 0 ? clamp01((elapsed - first) / span) : elapsed >= first ? 1 : 0;
    return { index: index, within: within, overall: overall };
  }

  function countdownParts(ms) {
    var s = Math.floor(Math.max(0, num(ms)) / 1000);
    return {
      d: Math.floor(s / 86400),
      h: Math.floor((s % 86400) / 3600),
      m: Math.floor((s % 3600) / 60),
      s: s % 60,
    };
  }

  function formatParts(iso, tz, options) {
    var t = parseISO(iso);
    if (isNaN(t)) return '';
    try {
      var opts = Object.assign({}, options);
      if (tz) opts.timeZone = tz;
      return new Intl.DateTimeFormat('es-ES', opts).format(new Date(t));
    } catch (e) {
      return '';
    }
  }

  // "18:30" en la zona indicada (o la del navegador si no se indica).
  function localTimeLabel(iso, tz) {
    return formatParts(iso, tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  }

  /* ------------------------------------------------------------------ */
  /* UTMs y fuente                                                       */
  /* ------------------------------------------------------------------ */

  function param(params, key) {
    if (!params) return '';
    var v = typeof params.get === 'function' ? params.get(key) : params[key];
    return v == null ? '' : String(v).trim();
  }

  // Regla de complementos.md §4 (por orden). fbclid no decide: también llega en orgánico.
  // Ampliación: un gclid sin utm_medium solo puede venir de Google Ads → paid_google.
  function resolveFuente(params) {
    var medium = param(params, 'utm_medium').toLowerCase();
    var source = param(params, 'utm_source').toLowerCase();
    var campaign = param(params, 'utm_campaign').toLowerCase();
    var gclid = !!param(params, 'gclid') || param(params, 'has_gclid') === 'true';
    if (campaign.indexOf('webinar-28oct-invitacion') === 0) return 'email_1clic';
    if (/^paid|^(cpc|ppc|cpm)$/.test(medium)) {
      return source === 'google' || source === 'youtube' || gclid ? 'paid_google' : 'paid_meta';
    }
    if (!medium && gclid) return 'paid_google';
    return 'organico';
  }

  function emptyUtm() {
    var out = {};
    UTM_KEYS.forEach(function (k) {
      out[k] = '';
    });
    out.has_fbclid = false;
    out.has_gclid = false;
    return out;
  }

  // Lee las UTMs guardadas al llegar. Nunca lanza.
  function readUtm(storage) {
    var out = emptyUtm();
    try {
      var raw = storage && storage.getItem(UTM_STORE);
      if (!raw) return out;
      var saved = JSON.parse(raw) || {};
      UTM_KEYS.forEach(function (k) {
        if (typeof saved[k] === 'string') out[k] = saved[k].slice(0, 200);
      });
      out.has_fbclid = saved.has_fbclid === true;
      out.has_gclid = saved.has_gclid === true;
    } catch (e) {
      /* almacenamiento bloqueado o JSON roto: UTMs vacías */
    }
    return out;
  }

  // Guarda las UTMs de la URL de llegada. Una página sin UTMs no borra las anteriores.
  // De fbclid/gclid solo se guarda si estaban (nunca su valor).
  function captureUtm(storage, params) {
    var data = emptyUtm();
    var any = false;
    UTM_KEYS.forEach(function (k) {
      data[k] = param(params, k).slice(0, 200);
      if (data[k]) any = true;
    });
    data.has_fbclid = !!param(params, 'fbclid');
    data.has_gclid = !!param(params, 'gclid');
    if (!any && !data.has_fbclid && !data.has_gclid) return readUtm(storage);
    try {
      if (storage) storage.setItem(UTM_STORE, JSON.stringify(data));
    } catch (e) {
      /* sin almacenamiento: se usan solo para esta página */
    }
    return data;
  }

  /* ------------------------------------------------------------------ */
  /* URLs, calendario y compartir                                        */
  /* ------------------------------------------------------------------ */

  function absUrl(path, cfg) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    var origin = (cfg && cfg.origin) || (root.location && root.location.origin) || '';
    return origin.replace(/\/+$/, '') + (path.charAt(0) === '/' ? '' : '/') + path;
  }

  function eventInfo(cfg) {
    var c = cfg || {};
    var T = eventTimes(c);
    var url = absUrl((c.urls && c.urls.directo) || '/pages/webinar-directo', c);
    return {
      start: T.start,
      end: T.end,
      doors: T.doors,
      title: c.titulo || 'Directo con Elena Busto',
      details: c.calendario || '',
      url: url,
      uid: c.icsUid || 'wb1028@elena-busto.com',
    };
  }

  function utcBasic(ms) {
    return new Date(ms).toISOString().replace(/\.\d{3}/, '').replace(/[-:]/g, '');
  }

  function utcExtended(ms) {
    return new Date(ms).toISOString().replace(/\.\d{3}/, '');
  }

  function withUrl(details, url) {
    return details ? details + '\n\n' + url : url;
  }

  function googleCalendarUrl(cfg) {
    var e = eventInfo(cfg);
    if (isNaN(e.start)) return '';
    return (
      'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      '&text=' + encodeURIComponent(e.title) +
      '&dates=' + utcBasic(e.start) + '/' + utcBasic(e.end) +
      '&details=' + encodeURIComponent(withUrl(e.details, e.url)) +
      '&location=' + encodeURIComponent(e.url)
    );
  }

  function outlookCalendarUrl(cfg) {
    var e = eventInfo(cfg);
    if (isNaN(e.start)) return '';
    return (
      'https://outlook.live.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent' +
      '&subject=' + encodeURIComponent(e.title) +
      '&startdt=' + encodeURIComponent(utcExtended(e.start)) +
      '&enddt=' + encodeURIComponent(utcExtended(e.end)) +
      '&body=' + encodeURIComponent(withUrl(e.details, e.url)) +
      '&location=' + encodeURIComponent(e.url)
    );
  }

  function icsEscape(s) {
    return String(s)
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  function utf8Bytes(ch) {
    var c = ch.codePointAt(0);
    return c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }

  // RFC 5545 §3.1: líneas de 75 octetos como máximo; la continuación empieza por un espacio.
  function icsFold(line) {
    var out = '';
    var count = 0;
    for (var ch of line) {
      var b = utf8Bytes(ch);
      if (count + b > 75) {
        out += '\r\n ';
        count = 1;
      }
      out += ch;
      count += b;
    }
    return out;
  }

  // Texto .ics. UID fijo: si se importa dos veces, el calendario actualiza en vez de duplicar.
  // El aviso salta a la hora de abrir la sala.
  function icsText(cfg, stampMs) {
    var e = eventInfo(cfg);
    if (isNaN(e.start)) return '';
    var alarmMin = Math.max(5, Math.round((e.start - e.doors) / MIN) || 15);
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Elena Busto//Directo 11 noviembre 2026//ES',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + e.uid,
      'SEQUENCE:1', // 1: directo aplazado del 28-oct al 11-nov (mismo UID → actualiza el evento)
      'DTSTAMP:' + utcBasic(typeof stampMs === 'number' ? stampMs : Date.now()),
      'DTSTART:' + utcBasic(e.start),
      'DTEND:' + utcBasic(e.end),
      'SUMMARY:' + icsEscape(e.title),
      'DESCRIPTION:' + icsEscape(withUrl(e.details, e.url)),
      'LOCATION:' + icsEscape(e.url),
      'URL:' + e.url,
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + icsEscape(e.title),
      'TRIGGER:-PT' + alarmMin + 'M',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    return lines.map(icsFold).join('\r\n') + '\r\n';
  }

  // Enlace a la landing orgánica con UTMs de "compartir" (fuente = organico).
  function shareUrl(cfg, source, content) {
    var base = absUrl((cfg && cfg.urls && cfg.urls.landing) || '/pages/webinar', cfg);
    var qs =
      'utm_source=' + encodeURIComponent(source || 'enlace') +
      '&utm_medium=organic&utm_campaign=webinar-28oct-compartir' +
      (content ? '&utm_content=' + encodeURIComponent(content) : '');
    return base + (base.indexOf('?') === -1 ? '?' : '&') + qs;
  }

  /* ------------------------------------------------------------------ */
  /* DOM                                                                 */
  /* ------------------------------------------------------------------ */

  var doc = root.document;
  var configCache = null;

  function config() {
    if (configCache) return configCache;
    configCache = {};
    try {
      var el = doc && doc.getElementById('ebw-config');
      if (el) configCache = JSON.parse(el.textContent) || {};
    } catch (e) {
      warn('config', e);
    }
    return configCache;
  }

  function session() {
    try {
      return root.sessionStorage || null;
    } catch (e) {
      return null;
    }
  }

  function utm() {
    return readUtm(session());
  }

  function onReady(fn) {
    var run = function () {
      try {
        fn();
      } catch (e) {
        warn('onReady', e);
      }
    };
    if (doc && doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', run);
    else run();
  }

  function qsa(scope, selector) {
    var base = scope && scope.querySelectorAll ? scope : doc;
    var list = Array.prototype.slice.call(base.querySelectorAll(selector));
    if (base.matches && base.matches(selector)) list.unshift(base);
    return list;
  }

  function dispatch(target, name, detail) {
    try {
      target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    } catch (e) {
      warn(name, e);
    }
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  // Un único temporizador por página, alineado al segundo. Cada suscriptor recibe now().
  var tickers = [];
  var tickTimer = null;

  function tick() {
    var t = now();
    tickers.slice().forEach(function (fn) {
      try {
        fn(t);
      } catch (e) {
        warn('tick', e);
      }
    });
  }

  function scheduleTick() {
    tickTimer = setTimeout(function () {
      tick();
      scheduleTick();
    }, 1000 - (Date.now() % 1000) + 15);
  }

  function onTick(fn) {
    tickers.push(fn);
    try {
      fn(now());
    } catch (e) {
      warn('tick', e);
    }
    if (!tickTimer) {
      scheduleTick();
      if (doc) {
        doc.addEventListener('visibilitychange', function () {
          if (!doc.hidden) tick();
        });
      }
    }
  }

  function mountCountdowns(scope) {
    var cfg = config();
    qsa(scope, '[data-ebw-countdown]').forEach(function (el) {
      if (el.__ebwMounted) return;
      var key = el.getAttribute('data-target') || 'start';
      var target = parseISO(cfg[key]);
      if (isNaN(target)) return;
      el.__ebwMounted = true;
      var units = {};
      ['d', 'h', 'm', 's'].forEach(function (u) {
        units[u] = el.querySelector('[data-ebw-unit="' + u + '"]');
      });
      var done = false;
      el.classList.add('is-ticking');
      onTick(function (t) {
        if (done) return;
        var left = target - t;
        var p = countdownParts(left);
        setText(units.d, String(p.d));
        setText(units.h, pad2(p.h));
        setText(units.m, pad2(p.m));
        setText(units.s, pad2(p.s));
        el.classList.toggle('is-under-day', p.d === 0);
        el.classList.toggle('is-under-hour', p.d === 0 && p.h === 0);
        if (left <= 0) {
          done = true;
          el.classList.add('is-done');
          dispatch(el, 'ebw:countdown-done', { target: key });
        }
      });
    });
  }

  function isAppleMobile() {
    var nav = root.navigator || {};
    return /iP(hone|od|ad)/.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  }

  function downloadIcs(cfg, filename) {
    var text = icsText(cfg);
    // iOS no descarga Blobs: abre el .ics como data: y ofrece "Añadir al calendario".
    if (isAppleMobile()) {
      root.location.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(text);
      return;
    }
    var url = URL.createObjectURL(new Blob([text], { type: 'text/calendar;charset=utf-8' }));
    var a = doc.createElement('a');
    a.href = url;
    a.download = filename;
    a.hidden = true;
    doc.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(function () {
      URL.revokeObjectURL(url);
    }, 5000);
  }

  function mountCalendarButtons(scope) {
    var cfg = config();
    if (isNaN(eventTimes(cfg).start)) return; // sin fecha: los botones siguen ocultos o con su enlace de Liquid
    qsa(scope, '[data-ebw-calendar]').forEach(function (el) {
      if (el.__ebwMounted) return;
      el.__ebwMounted = true;
      var kind = (el.getAttribute('data-ebw-calendar') || 'google').toLowerCase();
      if (kind === 'ics') {
        el.hidden = false;
        el.addEventListener('click', function (ev) {
          ev.preventDefault();
          downloadIcs(cfg, el.getAttribute('data-filename') || 'directo-elena-busto-28-octubre.ics');
        });
        return;
      }
      var href = kind === 'outlook' ? outlookCalendarUrl(cfg) : googleCalendarUrl(cfg);
      if (el.tagName === 'A') {
        el.href = href;
        el.target = '_blank';
        el.rel = 'noopener';
      } else {
        el.addEventListener('click', function () {
          root.open(href, '_blank', 'noopener');
        });
      }
      el.hidden = false;
    });
  }

  function copyText(text) {
    if (root.navigator && root.navigator.clipboard && root.isSecureContext) {
      return root.navigator.clipboard.writeText(text).then(
        function () {
          return true;
        },
        function () {
          return legacyCopy(text);
        }
      );
    }
    return Promise.resolve(legacyCopy(text));
  }

  function legacyCopy(text) {
    var ta = doc.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.className = 'ebw-visually-hidden';
    doc.body.appendChild(ta);
    ta.select();
    var ok = false;
    try {
      ok = doc.execCommand('copy');
    } catch (e) {
      ok = false;
    }
    ta.remove();
    return ok;
  }

  function mountShare(scope) {
    var cfg = config();
    qsa(scope, '[data-ebw-share]').forEach(function (box) {
      if (box.__ebwMounted) return;
      box.__ebwMounted = true;
      var content = box.getAttribute('data-content') || '';
      var text = box.getAttribute('data-text') || [cfg.titulo, cfg.fechaTexto].filter(Boolean).join(' · ');
      var msg = box.querySelector('[data-ebw-share-msg]');
      var link = shareUrl(cfg, 'enlace', content);

      qsa(box, '[data-ebw-share-url]').forEach(function (el) {
        if ('value' in el) el.value = link;
        else el.textContent = link;
      });

      var wa = box.querySelector('[data-ebw-share-whatsapp]');
      if (wa) {
        wa.href = 'https://wa.me/?text=' + encodeURIComponent(text + ' ' + shareUrl(cfg, 'whatsapp', content));
        wa.target = '_blank';
        wa.rel = 'noopener';
      }

      var copy = box.querySelector('[data-ebw-share-copy]');
      if (copy) {
        copy.hidden = false;
        copy.addEventListener('click', function () {
          copyText(link).then(function (ok) {
            setText(msg, ok
              ? copy.getAttribute('data-ok') || 'Enlace copiado.'
              : (copy.getAttribute('data-fail') || 'No he podido copiarlo. Este es el enlace:') + ' ' + link);
          });
        });
      }

      var native = box.querySelector('[data-ebw-share-native]');
      if (native && root.navigator && typeof root.navigator.share === 'function') {
        native.hidden = false;
        native.addEventListener('click', function () {
          root.navigator
            .share({ title: cfg.titulo || '', text: text, url: shareUrl(cfg, 'nativo', content) })
            .catch(function () {});
        });
      }
    });
  }

  var landingCurrent = null;
  var landingMounted = false;

  function applyLandingState(state) {
    if (state === landingCurrent) return;
    var previous = landingCurrent;
    landingCurrent = state;
    doc.body.setAttribute('data-state', state);
    qsa(doc, '[data-ebw-show-when]').forEach(function (el) {
      var states = (el.getAttribute('data-ebw-show-when') || '').split(/\s+/);
      el.hidden = states.indexOf(state) === -1;
    });
    dispatch(doc, 'ebw:landing-state', { state: state, previous: previous });
  }

  function mountLandingState(scope) {
    if (!qsa(scope, '[data-ebw-landing-state]').length) return;
    if (landingMounted) {
      // Sección recargada en el editor: reaplicar al contenido nuevo.
      var keep = landingCurrent;
      landingCurrent = null;
      applyLandingState(keep);
      return;
    }
    landingMounted = true;
    var cfg = config();
    onTick(function (t) {
      applyLandingState(landingState(t, cfg));
    });
  }

  function landingStateNow() {
    return landingState(now(), config());
  }

  function mountLocalTimes(scope) {
    var cfg = config();
    var eventTz = cfg.timezone || EVENT_TZ;
    qsa(scope, '[data-ebw-localtime]').forEach(function (el) {
      var iso = cfg[el.getAttribute('data-ebw-localtime') || 'start'];
      var mine = localTimeLabel(iso);
      if (!mine || mine === localTimeLabel(iso, eventTz)) return;
      var dayOpts = { weekday: 'long', day: 'numeric' };
      var myDay = formatParts(iso, null, dayOpts);
      var label = (myDay !== formatParts(iso, eventTz, dayOpts) ? myDay + ', ' + mine : mine) + ' h';
      var template = el.getAttribute('data-template') || 'En tu zona horaria: {hora}';
      el.textContent = template.replace('{hora}', label);
      el.hidden = false;
    });
  }

  function mountReveal(scope) {
    var els = qsa(scope, '.ebw-reveal');
    if (!els.length) return;
    var reduce = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in root)) {
      els.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    // Lo que ya se ve no se oculta (evita el parpadeo); solo se anima lo que está más abajo.
    var vh = root.innerHeight || 0;
    els.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) el.classList.add('is-in');
    });
    doc.documentElement.classList.add('ebw-reveal-on');
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach(function (el) {
      if (!el.classList.contains('is-in')) io.observe(el);
    });
  }

  function mountAll(scope) {
    [mountReveal, mountCountdowns, mountCalendarButtons, mountShare, mountLandingState, mountLocalTimes].forEach(
      function (fn) {
        try {
          fn(scope);
        } catch (e) {
          warn('mount', e);
        }
      }
    );
  }

  function boot() {
    try {
      captureUtm(session(), new URLSearchParams(root.location.search));
    } catch (e) {
      warn('utm', e);
    }
    onReady(function () {
      mountAll(doc);
    });
    // Editor de temas: al recargar una sección, se vuelve a montar solo esa sección.
    doc.addEventListener('shopify:section:load', function (ev) {
      mountAll(ev.target);
    });
  }

  return {
    version: '1.0.0',
    // reloj y estados
    now: now,
    parseISO: parseISO,
    setServerOffset: setServerOffset,
    isQaClock: isQaClock,
    landingState: landingState,
    roomState: roomState,
    phaseAt: phaseAt,
    countdownParts: countdownParts,
    localTimeLabel: localTimeLabel,
    // UTMs y fuente
    resolveFuente: resolveFuente,
    captureUtm: captureUtm,
    readUtm: readUtm,
    utm: utm,
    // calendario y compartir
    googleCalendarUrl: googleCalendarUrl,
    outlookCalendarUrl: outlookCalendarUrl,
    icsText: icsText,
    shareUrl: shareUrl,
    absUrl: absUrl,
    // DOM
    config: config,
    onReady: onReady,
    onTick: onTick,
    landingStateNow: landingStateNow,
    mountAll: mountAll,
    mountCountdowns: mountCountdowns,
    mountCalendarButtons: mountCalendarButtons,
    mountShare: mountShare,
    mountLandingState: mountLandingState,
    mountLocalTimes: mountLocalTimes,
    mountReveal: mountReveal,
    _boot: boot,
  };
});
